/** Event floorplans: shapes drawn on a grid, saved with the event request. */

export type ShapeKind = 'rect' | 'circle' | 'text' | 'line';

export interface FloorplanShape {
  id: string;
  kind: ShapeKind;
  /** The top-left of the shape's box, or where a line starts. */
  x: number;
  y: number;
  /** The box's size, or how far a line runs from its start (either can be negative). */
  w: number;
  h: number;
  /** Degrees, around the box's center. Lines don't turn. */
  rotation: number;
  /** What a shape says, or a text box's words. */
  label: string;
  fontSize: number;
  /** A hex color, or "none". */
  fill: string;
  /** The outline, line, or text color. */
  stroke: string;
  dashed: boolean;
  /** Lines only: an arrowhead at the end. */
  arrow: boolean;
}

export interface Floorplan {
  version: 1;
  width: number;
  height: number;
  shapes: FloorplanShape[];
}

export const FLOORPLAN_WIDTH = 1200;
export const FLOORPLAN_HEIGHT = 800;
/** Grid lines, and the step shapes snap to. */
export const GRID = 20;
export const SNAP = 10;
export const MAX_SHAPES = 500;

export const emptyFloorplan = (): Floorplan => ({ version: 1, width: FLOORPLAN_WIDTH, height: FLOORPLAN_HEIGHT, shapes: [] });

export const SHAPE_NAME: Record<ShapeKind, string> = { rect: 'Rectangle', circle: 'Circle', text: 'Text', line: 'Line' };

export const FILLS = [
  { value: 'none', label: 'No fill' },
  { value: '#ffffff', label: 'White' },
  { value: '#e2e8f0', label: 'Gray' },
  { value: '#bfdbfe', label: 'Blue' },
  { value: '#bbf7d0', label: 'Green' },
  { value: '#fde68a', label: 'Yellow' },
  { value: '#fed7aa', label: 'Orange' },
  { value: '#fecaca', label: 'Red' },
  { value: '#ddd6fe', label: 'Purple' },
];

export const INKS = [
  { value: '#0f172a', label: 'Black' },
  { value: '#64748b', label: 'Gray' },
  { value: '#1d4ed8', label: 'Blue' },
  { value: '#15803d', label: 'Green' },
  { value: '#b45309', label: 'Brown' },
  { value: '#b91c1c', label: 'Red' },
  { value: '#6d28d9', label: 'Purple' },
];

/** Label and text sizes. */
export const FONT_SIZES = [
  { value: 12, label: 'S' },
  { value: 16, label: 'M' },
  { value: 24, label: 'L' },
  { value: 36, label: 'XL' },
];

const DEFAULTS: Record<ShapeKind, Partial<FloorplanShape>> = {
  rect: { w: 160, h: 80, fill: '#ffffff', fontSize: 16 },
  circle: { w: 100, h: 100, fill: '#ffffff', fontSize: 16 },
  text: { w: 0, h: 0, fill: 'none', label: 'Text', fontSize: 24 },
  line: { w: 160, h: 0, fill: 'none', fontSize: 16 },
};

let counter = 0;
export const shapeId = () => `s${Date.now().toString(36)}${(counter++).toString(36)}`;

export function newShape(kind: ShapeKind, x: number, y: number, patch: Partial<FloorplanShape> = {}): FloorplanShape {
  return {
    id: shapeId(),
    kind,
    x,
    y,
    w: 0,
    h: 0,
    rotation: 0,
    label: '',
    fontSize: 16,
    fill: 'none',
    stroke: '#0f172a',
    dashed: false,
    arrow: false,
    ...DEFAULTS[kind],
    ...patch,
  };
}

/** Pieces most event layouts need, ready to drop in. */
export const PRESETS: { name: string; shape: Partial<FloorplanShape> & { kind: ShapeKind } }[] = [
  { name: 'Stage', shape: { kind: 'rect', w: 320, h: 120, label: 'Stage', fill: '#ddd6fe', fontSize: 24 } },
  { name: 'Table', shape: { kind: 'rect', w: 120, h: 60, label: 'Table', fill: '#fde68a' } },
  { name: 'Round table', shape: { kind: 'circle', w: 100, h: 100, label: 'Table', fill: '#fde68a' } },
  { name: 'Row of chairs', shape: { kind: 'rect', w: 240, h: 30, label: 'Chairs', fill: '#bfdbfe', fontSize: 12, dashed: true } },
  { name: 'Podium', shape: { kind: 'rect', w: 60, h: 40, label: 'Podium', fill: '#e2e8f0', fontSize: 12 } },
  { name: 'Screen', shape: { kind: 'rect', w: 200, h: 20, label: 'Screen', fill: '#e2e8f0', fontSize: 12 } },
  { name: 'Speaker', shape: { kind: 'circle', w: 40, h: 40, label: 'Spkr', fill: '#e2e8f0', fontSize: 12 } },
  { name: 'Entrance', shape: { kind: 'line', w: 0, h: -80, label: 'Entrance', arrow: true, stroke: '#15803d' } },
];

/** How much room a text box takes, from its words and size. */
export function textBox(shape: Pick<FloorplanShape, 'label' | 'fontSize'>) {
  const lines = (shape.label || ' ').split('\n');
  const longest = Math.max(1, ...lines.map((line) => line.length));
  return { w: Math.max(shape.fontSize, longest * shape.fontSize * 0.58), h: lines.length * shape.fontSize * 1.2 };
}

/** The box a shape covers, with a positive size. */
export function boxOf(shape: FloorplanShape) {
  if (shape.kind === 'text') return { x: shape.x, y: shape.y, ...textBox(shape) };
  return { x: Math.min(shape.x, shape.x + shape.w), y: Math.min(shape.y, shape.y + shape.h), w: Math.abs(shape.w), h: Math.abs(shape.h) };
}

const round = (value: number) => Math.round(value * 10) / 10;

/** A shape as it's saved: boxes with a positive size and tidy numbers. */
export function normalizeShape(shape: FloorplanShape): FloorplanShape {
  const box = shape.kind === 'line' ? { x: shape.x, y: shape.y, w: shape.w, h: shape.h } : boxOf(shape);
  return {
    ...shape,
    x: round(box.x),
    y: round(box.y),
    w: round(box.w),
    h: round(box.h),
    rotation: ((Math.round(shape.rotation) % 360) + 360) % 360,
    label: shape.label.slice(0, 200),
  };
}

const fileSafe = (name: string) => name.replace(/[^\w .-]+/g, '').trim().slice(0, 80) || 'floorplan';

/** Saves a drawn floorplan as a PNG, at twice its size so it prints sharply. */
export async function downloadFloorplan(svg: SVGSVGElement, plan: Floorplan, name: string) {
  const scale = 2;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('width', String(plan.width * scale));
  clone.setAttribute('height', String(plan.height * scale));
  clone.removeAttribute('class');
  clone.removeAttribute('style');
  const source = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('The floorplan couldn’t be drawn as an image'));
      img.src = source;
    });
    const canvas = document.createElement('canvas');
    canvas.width = plan.width * scale;
    canvas.height = plan.height * scale;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('This browser can’t save images');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!png) throw new Error('The floorplan couldn’t be saved as an image');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(png);
    link.download = `${fileSafe(name)}.png`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  } finally {
    URL.revokeObjectURL(source);
  }
}
